import React, { useState, useEffect } from "react"
import styled, { css, withTheme } from "styled-components"
import PropTypes from "prop-types"
import { FormattedMessage, Link } from "gatsby-plugin-react-intl"
import { use100vh } from "react-div-100vh"

import { Container } from "../components/Grid"
import NavigationMain from "../components/NavigationMain"
import HeaderNavToggle from "../components/HeaderNavToggle"
import StyledButton from "../components/Button"

// import XmasBalls from "./campaigns/XmasBalls"

import logo from "../images/logos/logo-bsdex.svg"
import logoWhite from "../images/logos/logo-bsdex-white.svg"

import Config from "../helpers/config"
import { media } from "../styles"

const HeaderContainer = styled.div`
  width: 100%;
`

const HeaderContent = styled.div`
  align-items: center;
  display: flex;
  flex-flow: row wrap;
  height: ${({ theme }) => theme.headerHeightXs}px;

  @media ${media.sm} {
    flex-flow: row nowrap;
    height: ${({ theme }) => theme.headerHeightSm}px;
    justify-content: space-between;
  }

  @media ${media.md} {
    height: ${({ theme }) => theme.headerHeightMd}px;
  }

  @media ${media.lg} {
    height: ${({ theme }) => theme.headerHeightLg}px;
  }
`

const Logo = styled.div`
  width: 80px;

  img {
    display: block;
    height: auto;
    max-width: 100%;
  }

  @media ${media.sm} {
    width: 100px;
  }

  @media ${media.tablet} {
    flex: 0 0 auto;
  }

  @media ${media.desktop} {
    width: 127px;
  }
`

const HeaderNavContainer = styled.div`
  align-items: center;
  display: flex;
  flex-direction: row;
  margin: 0 0 0 auto;

  @media ${media.lgDown} {
    align-items: flex-start;
    background: ${({ theme }) => theme.brandPrimaryDark};
    display: none;
    flex-direction: column;
    height: ${(props) =>
      props.fullMobileHeight ? `${props.fullMobileHeight}px` : `100vh`};
    left: 0;
    margin: 0;
    overflow-y: auto;
    padding: 70px 0 25px;
    position: absolute;
    top: 0;
    -webkit-overflow-scrolling: touch;
    width: 100vw;
    z-index: 9;
  }

  ${(props) =>
    props.open &&
    css`
      @media ${media.lgDown} {
        display: flex;
      }
    `}
`

const HeaderNavButtons = styled.div`
  margin: 0 0 0 auto;
  order: 1;
  position: relative;

  @media ${media.tabletLandscape} {
    align-self: auto;
    display: flex;
    justify-content: space-between;
    flex: 1 1 345px;
    margin: 0 0 0 20px;
    max-width: 230px;
  }

  @media ${media.desktop} {
    flex: 1 1 390px;
    margin-left: 40px;
    max-width: 290px;
  }

  & > * {
    margin: 0 10px;
    /* splitting out flex shorthand for IE11 */
    flex-grow: 1;
    flex-shrink: 0;
    /* flex-basis: calc(50% - 20px); */

    @media ${media.desktop} {
      /* flex-basis: calc(50% - 30px); */
    }

    &:first-child {
      margin-left: 0;
    }

    &:last-child {
      margin-right: 0;
    }
  }

  & > a {
    font-size: 14px;

    @media ${media.desktop} {
      font-size: 16px;
    }
  }
`

const HeaderButton = styled(StyledButton)`
  @media ${media.lgDown} {
    padding-left: 30px;
    padding-right: 30px;

    > span {
      margin: 0;
    }

    svg {
      display: none;
    }
  }
`

const LoginButton = styled(HeaderButton)`
  ${HeaderNavButtons} & {
    @media ${media.lg} {
      margin: 0 0 0 15px;
    }

    @media ${media.desktop} {
      margin: 0 0 0 30px;
    }
  }
`

const RegisterButton = styled(HeaderButton)`
  ${HeaderNavButtons} & {
    @media ${media.smDown} {
      display: none;
    }

    @media ${media.lg} {
      margin: 0;
    }
  }
`

const NavigationButtons = styled.div`
  display: flex;
  margin-top: auto;
  flex-direction: column;
  padding: 0 15px;
  width: 100%;

  ${HeaderButton} {
    border: 1px solid ${({ theme }) => theme.brandPrimaryLight} !important;
    color: ${({ theme }) => theme.brandPrimaryLight};
    margin: 15px 0 0 0;
    justify-content: center;
    width: 100%;
  }

  @media ${media.tabletLandscape} {
    display: none;
  }
`

const ContactButton = styled(HeaderButton)`
  margin-left: auto !important;
  max-width: 215px;
  padding-left: 12px;
  padding-right: 12px;
`

const Header = ({ layout, siteTitle, theme, pageId }) => {
  const [isOpen, setIsOpen] = useState(false)
  const fullMobileHeight = use100vh() // workaround for Mobile Safari 100vh issue

  // default settings
  const display = {
    mainNav: true,
    langSwitcher: true,
    buttons: "standard",
  }

  switch (layout) {
    case "Landing Page":
      // hide header elements if we're on a landing page
      display.mainNav = false
      break
    case "Institutions":
      display.buttons = "b2b"
      break
    case "monolingual":
      display.langSwitcher = false
      break
    default:
      break
  }

  useEffect(() => {
    document.querySelector("body").classList.remove("has--nav-open")
  }, [])

  const handleToggle = () => {
    setIsOpen(!isOpen)
    document.querySelector("body").classList.toggle("has--nav-open")
  }

  return (
    <HeaderContainer>
      <Container>
        <HeaderContent>
          <Logo>
            <Link to="/">
              <img
                src={theme.name === "dark" ? logoWhite : logo}
                alt={siteTitle}
              />
            </Link>
          </Logo>
          {display.mainNav && (
            <HeaderNavContainer
              fullMobileHeight={fullMobileHeight}
              open={isOpen}
            >
              <NavigationMain displayLangSwitcher={display.langSwitcher} />
              <NavigationButtons>
                <RegisterButton
                  arrow
                  href={Config().linkPlatformSignup}
                  kind="primary"
                >
                  <FormattedMessage id="register" />
                </RegisterButton>
                <LoginButton
                  arrow
                  href={Config().linkPlatformLogin}
                  kind="outline"
                >
                  <FormattedMessage id="login" />
                </LoginButton>
              </NavigationButtons>
            </HeaderNavContainer>
          )}
          <HeaderNavButtons buttons={display.buttons}>
            {display.buttons === "b2b" ? (
              <ContactButton arrow href="mailto:b2b@bsdex.de" kind="primary">
                <FormattedMessage id="contactUs" />
              </ContactButton>
            ) : (
              <>
                <RegisterButton
                  arrow
                  href={Config().linkPlatformSignup}
                  kind="primary"
                >
                  <FormattedMessage id="register" />
                </RegisterButton>
                <LoginButton
                  arrow
                  href={Config().linkPlatformLogin}
                  kind="outline"
                >
                  <FormattedMessage id="login" />
                </LoginButton>
              </>
            )}
            {/* <XmasBalls pageId={pageId} /> */}
          </HeaderNavButtons>
          {display.mainNav && (
            <HeaderNavToggle
              open={isOpen}
              className={isOpen ? "open" : ""}
              handleToggle={handleToggle}
            />
          )}
        </HeaderContent>
      </Container>
    </HeaderContainer>
  )
}

Header.propTypes = {
  siteTitle: PropTypes.string.isRequired,
  pageId: PropTypes.string.isRequired,
  layout: PropTypes.string,
}

Header.defaultProps = {
  siteTitle: ``,
}

export default withTheme(Header)
